import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "./AuthContext";
import { getSpeak2Profile } from "../Apps/Speak2ContentAPIs/Speak2Profile";
import { fetchCorporateFacilities } from "../Apps/Speak2ContentAPIs/Corporates";
import {
  clearActiveCorporateFacility,
  isCorporateProfile,
  readActiveCorporateFacility,
  writeActiveCorporateFacility,
} from "../utils/corporateFacilityStorage";

const CorporateFacilityContext = createContext(null);
export default CorporateFacilityContext;

export const CorporateFacilityContextProvider = ({ children }) => {
  const { IsAuthenticated, IsSpeak2User, UserId, IsAuthChecking } = useContext(AuthContext);

  const [isCorporateUser, setIsCorporateUser] = useState(false);
  const [corporateId, setCorporateId] = useState(null);
  const [corporateName, setCorporateName] = useState(null);
  const [corporateFacilities, setCorporateFacilities] = useState([]);
  const [activeFacility, setActiveFacilityState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState(null);
  const [profileReady, setProfileReady] = useState(true);
  // Non-corporate users have a single, fixed facility on their own profile
  // (not the corporate active-facility picker). Resolved here so consumers
  // like useFacilityContext don't need their own redundant profile fetch.
  const [nonCorporateFacilityId, setNonCorporateFacilityId] = useState(null);
  const [nonCorporateTimezone, setNonCorporateTimezone] = useState(null);

  const hydrateActiveFromStorage = useCallback(() => {
    if (!UserId) {
      setActiveFacilityState(null);
      return;
    }
    const stored = readActiveCorporateFacility(UserId);
    setActiveFacilityState(stored);
  }, [UserId]);

  const loadCorporateState = useCallback(async () => {
    if (!IsAuthenticated || !IsSpeak2User || !UserId) {
      setIsCorporateUser(false);
      setCorporateId(null);
      setCorporateName(null);
      setCorporateFacilities([]);
      setActiveFacilityState(null);
      setNonCorporateFacilityId(null);
      setNonCorporateTimezone(null);
      setProfileReady(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setFacilitiesError(null);
    setProfileReady(false);

    try {
      const res = await getSpeak2Profile();
      if (res?.data?.Success !== true) {
        setIsCorporateUser(false);
        setProfileReady(true);
        return;
      }

      const content = res.data.Content;
      const corporate = isCorporateProfile(content);
      setIsCorporateUser(corporate);
      setCorporateId(content?.CorporateId ?? null);
      setCorporateName(content?.CorporateName ?? null);

      hydrateActiveFromStorage();

      if (corporate) {
        setNonCorporateFacilityId(null);
        setNonCorporateTimezone(null);
        const { success, facilities, error } = await fetchCorporateFacilities();
        if (!success) {
          setFacilitiesError(error ?? "Failed to load communities");
          setCorporateFacilities([]);
        } else {
          setCorporateFacilities(facilities);
        }
      } else {
        setCorporateFacilities([]);
        setNonCorporateFacilityId(content?.Facility ?? null);
        setNonCorporateTimezone(content?.FacilityTimeZone ?? null);
      }
    } catch (e) {
      console.log("CorporateFacilityContext load error", e);
      setFacilitiesError("Failed to load profile");
    } finally {
      setLoading(false);
      setProfileReady(true);
    }
  }, [IsAuthenticated, IsSpeak2User, UserId, hydrateActiveFromStorage]);

  useEffect(() => {
    if (IsAuthChecking) return;
    loadCorporateState();
  }, [IsAuthChecking, loadCorporateState]);

  useEffect(() => {
    hydrateActiveFromStorage();
  }, [UserId, hydrateActiveFromStorage]);

  const setActiveFacility = useCallback(
    (facility) => {
      if (!UserId || !facility?._id) return;
      const payload = {
        facilityId: facility._id,
        facilityName: facility.Name,
        timezone: facility.TimeZone ?? null,
        corporateId: corporateId,
      };
      writeActiveCorporateFacility(UserId, payload);
      setActiveFacilityState(payload);
    },
    [UserId, corporateId],
  );

  const clearActiveFacility = useCallback(() => {
    if (UserId) {
      clearActiveCorporateFacility(UserId);
    }
    setActiveFacilityState(null);
  }, [UserId]);

  const requiresFacilitySelection = useMemo(() => {
    if (!profileReady || !isCorporateUser) return false;
    return !activeFacility?.facilityId;
  }, [profileReady, isCorporateUser, activeFacility]);

  const value = useMemo(
    () => ({
      isCorporateUser,
      corporateId,
      corporateName,
      corporateFacilities,
      activeFacility,
      setActiveFacility,
      clearActiveFacility,
      loading,
      facilitiesError,
      profileReady,
      requiresFacilitySelection,
      reloadCorporateState: loadCorporateState,
      nonCorporateFacilityId,
      nonCorporateTimezone,
    }),
    [
      isCorporateUser,
      corporateId,
      corporateName,
      corporateFacilities,
      activeFacility,
      setActiveFacility,
      clearActiveFacility,
      loading,
      facilitiesError,
      profileReady,
      requiresFacilitySelection,
      loadCorporateState,
      nonCorporateFacilityId,
      nonCorporateTimezone,
    ],
  );

  return (
    <CorporateFacilityContext.Provider value={value}>{children}</CorporateFacilityContext.Provider>
  );
};

export function useCorporateFacility() {
  const ctx = useContext(CorporateFacilityContext);
  if (!ctx) {
    throw new Error("useCorporateFacility must be used within CorporateFacilityContextProvider");
  }
  return ctx;
}
